import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { Toast } from '../../../components';
import useToast from '../../../hooks/useToast';
import AddVisit from './AddVisit';
import { addStockTransaction, updateProductStock, TRANSACTION_TYPES, calculateStockSummary } from '../../../utils/stockUtils';
import { generateInvoiceNumber } from '../../../utils/invoiceUtils';
import { useAuth } from '../../../contexts/AuthContext';

function AddVisitContainer() {
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const [loading, setLoading] = useState(false);
  const { toast, showSuccess, showError, hideToast } = useToast();
  const [doctors, setDoctors] = useState([]);
  const [products, setProducts] = useState([]);
  const [doctorSearch, setDoctorSearch] = useState('');
  const [showDoctorDropdown, setShowDoctorDropdown] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [formData, setFormData] = useState({
    doctor_id: '',
    visit_date: new Date().toISOString().split('T')[0],
    notes: '',
    status: 'completed'
  });
  const [sales, setSales] = useState([]);
  const [newSale, setNewSale] = useState({
    product_id: '',
    quantity: '',
    unit_price: ''
  })
  const [currentStock, setCurrentStock] = useState(null);
  const [discountPercentage, setDiscountPercentage] = useState('');
  const canManageSales = role === 'owner';

  // Unit price = MRP minus the contact's discount %. Fall back to the product's
  // price if no MRP is set. Returns a 2-decimal string for the price input.
  const computeUnitPrice = (product, discountPct) => {
    const base = parseFloat(product?.mrp) || parseFloat(product?.price) || 0;
    const pct = parseFloat(discountPct) || 0;
    return (base * (1 - pct / 100)).toFixed(2);
  };

  useEffect(() => {
    fetchDoctors();
    if (canManageSales) {
      fetchProducts();
    } else {
      setProducts([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageSales]);

  const fetchDoctors = async () => {
    try {
      const { data, error } = await supabase
        .from('doctors')
        .select('id, name, specialization, hospital, doctor_type, doctor_class, contact_type, discount_percentage')
        .order('name');

      if (error) throw error;
      setDoctors(data || []);
    } catch (error) {
      console.error('Error fetching doctors:', error);
      showError('Error loading contacts');
    }
  };

  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('id, name, price, mrp, current_stock')
        .order('name');

      if (error) throw error;
      setProducts(data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      showError('Error loading products');
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaleChange = async (e) => {
    const { name, value } = e.target;
    setNewSale(prev => ({
      ...prev,
      [name]: name === 'quantity' || name === 'unit_price'
        ? value
        : value
    }));

    if (name === 'product_id' && value) {
      try {
        const today = new Date().toISOString().split('T')[0];
        const stockSummary = await calculateStockSummary(value, today);
        setCurrentStock(stockSummary.closingStock);
      } catch (error) {
        console.error('Error checking stock:', error);
        setCurrentStock(null);
      }
    }
  };

  const handleProductSearchChange = (e) => {
    setProductSearch(e.target.value);
    setShowProductDropdown(true);
    if (!e.target.value) {
      setNewSale(prev => ({ ...prev, product_id: '' }));
      setCurrentStock(null);
    }
  };

  const handleProductSelect = async (product) => {
    const unitPrice = computeUnitPrice(product, discountPercentage);
    const mrpLabel = product.mrp ? parseFloat(product.mrp).toFixed(2) : parseFloat(product.price || 0).toFixed(2);
    setNewSale(prev => ({
      ...prev,
      product_id: product.id,
      unit_price: unitPrice
    }));
    setProductSearch(`${product.name} - MRP ₹${mrpLabel} (Stock: ${product.current_stock || 0})`);
    setShowProductDropdown(false);

    try {
      const today = new Date().toISOString().split('T')[0];
      const stockSummary = await calculateStockSummary(product.id, today);
      setCurrentStock(stockSummary.closingStock);
    } catch (error) {
      console.error('Error checking stock:', error);
      setCurrentStock(null);
    }
  };

  const addSale = () => {
    const quantity = parseFloat(newSale.quantity);
    const unit_price = parseFloat(newSale.unit_price);

    if (!newSale.product_id || !newSale.quantity || isNaN(quantity) || quantity <= 0 || !newSale.unit_price || isNaN(unit_price) || unit_price <= 0) {
      showError('Please fill in all sale details');
      return;
    }

    if (currentStock !== null && quantity > currentStock) {
      showError(`Insufficient stock. Available: ${currentStock}`);
      return;
    }

    const product = products.find(p => p.id === newSale.product_id);
    const total_amount = quantity * unit_price;

    setSales(prev => [...prev, {
      id: Date.now(),
      product_id: newSale.product_id,
      quantity: quantity,
      unit_price: unit_price,
      total_amount,
      product_name: product?.name
    }]);


    setNewSale({
      product_id: '',
      quantity: '',
      unit_price: ''
    });
    setProductSearch('');
    setCurrentStock(null);
  };

  const removeSale = (index) => {
    setSales(prev => prev.filter((_, i) => i !== index));
  };

  // Core save logic: contact discount, stock check, visit, sales, ledger, stock transactions
  const saveVisitData = async (visitFormData, visitSales, visitDiscount) => {
    const safeSales = canManageSales ? visitSales : [];
    const visitPayload = {
      ...visitFormData,
      user_id: user?.id
    };

    if (canManageSales && visitFormData.doctor_id && visitDiscount !== '' && visitDiscount != null) {
      const pct = parseFloat(visitDiscount);
      if (!isNaN(pct)) {
        const { error: discountError } = await supabase
          .from('doctors')
          .update({ discount_percentage: pct })
          .eq('id', visitFormData.doctor_id);
        if (discountError) {
          console.error('Error saving contact discount:', discountError);
        }
      }
    }

    if (safeSales.length > 0) {
      const requestedByProduct = safeSales.reduce((acc, s) => {
        acc[s.product_id] = (acc[s.product_id] || 0) + parseFloat(s.quantity || 0);
        return acc;
      }, {});

      for (const productId of Object.keys(requestedByProduct)) {
        const today = new Date().toISOString().split('T')[0];
        const stockSummary = await calculateStockSummary(productId, today);
        const requested = requestedByProduct[productId];
        if (stockSummary.closingStock < requested) {
          const productName =
            safeSales.find(s => s.product_id === productId)?.product_name || 'product';
          throw new Error(
            `Insufficient stock for ${productName}. Available: ${stockSummary.closingStock}, Required: ${requested}`
          );
        }
      }
    }

    // Generate invoice number
    let invoiceNumber = null;
    if (safeSales.length > 0) {
      invoiceNumber = await generateInvoiceNumber();
      if (!invoiceNumber) {
        throw new Error('Failed to generate invoice number');
      }
    }

    // Insert visit
    const { data: visit, error: visitError } = await supabase
      .from('visits')
      .insert([visitPayload])
      .select()
      .single();

    if (visitError) throw visitError;

    // Insert sales and update stock if any
    if (safeSales.length > 0 && invoiceNumber) {
      const salesData = safeSales.map(sale => ({
        visit_id: visit.id,
        product_id: sale.product_id,
        quantity: sale.quantity,
        unit_price: sale.unit_price,
        total_amount: sale.total_amount
      }));

      const { error: salesError } = await supabase
        .from('sales')
        .insert(salesData);

      if (salesError) throw salesError;

      const totalSalesAmount = safeSales.reduce((sum, s) => sum + s.total_amount, 0);

      // Create ledger entry for the sale (DEBIT - Customer owes us money)
      const { error: ledgerError } = await supabase.from('ledger_entries').insert({
        doctor_id: visitFormData.doctor_id,
        entry_date: visitFormData.visit_date,
        source_type: 'visit',
        source_id: visit.id,
        description: `Sales from visit - ${visitSales.length} items (Invoice: ${invoiceNumber})`,
        debit: totalSalesAmount,
        credit: 0,
        invoice_number: invoiceNumber
      });

      if (ledgerError) {
        console.error('Ledger entry error:', ledgerError);
        throw new Error('Failed to create ledger entry');
      }

      // Add stock transactions for each sale
      for (const sale of safeSales) {
        await addStockTransaction({
          product_id: sale.product_id,
          transaction_type: TRANSACTION_TYPES.SALE,
          quantity: -sale.quantity,
          transaction_date: visitFormData.visit_date,
          notes: `Sale via visit to contact ID: ${visitFormData.doctor_id} - Invoice: ${invoiceNumber}`
        });

        await updateProductStock(sale.product_id);
      }
    }

    return invoiceNumber;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.doctor_id) {
      showError('Please select a contact');
      return;
    }

    setLoading(true);

    try {
      const invoiceNumber = await saveVisitData(formData, sales, discountPercentage);
      showSuccess(`Visit added successfully! ${invoiceNumber ? `Invoice: ${invoiceNumber}` : ''}`);
      navigate('/visits');
    } catch (error) {
      console.error('Error adding visit:', error);
      showError(error.message || 'Error adding visit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const totalSalesAmount = sales.reduce((total, sale) => total + sale.total_amount, 0);

  const filteredDoctors = doctors.filter(doctor => {
    const searchLower = doctorSearch.toLowerCase();
    const isChemist = doctor.contact_type === 'chemist';
    
    return (
      doctor.name.toLowerCase().includes(searchLower) ||
      doctor.hospital?.toLowerCase().includes(searchLower) ||
      (!isChemist && doctor.specialization?.toLowerCase().includes(searchLower)) ||
      (!isChemist && doctor.doctor_type?.toLowerCase().includes(searchLower)) ||
      (!isChemist && doctor.doctor_class?.toLowerCase().includes(searchLower))
    );
  });

  const filteredProducts = products.filter(product => 
    product.name.toLowerCase().includes(productSearch.toLowerCase())
  );

  const handleDoctorSearchChange = (e) => {
    setDoctorSearch(e.target.value);
    setShowDoctorDropdown(true);
    if (!e.target.value) {
      setFormData(prev => ({ ...prev, doctor_id: '' }));
      setDiscountPercentage('');
    }
  };

  const handleDiscountChange = (e) => {
    const value = e.target.value;
    setDiscountPercentage(value);
    if (newSale.product_id) {
      const product = products.find(p => p.id === newSale.product_id);
      if (product) {
        setNewSale(prev => ({ ...prev, unit_price: computeUnitPrice(product, value) }));
      }
    }
  };

  const formatDoctorDisplay = (doctor) => {
    const isChemist = doctor.contact_type === 'chemist';
    if (isChemist) {
      return `${doctor.name}${doctor.hospital ? ` - ${doctor.hospital}` : ''} [Chemist]`;
    }
    return `${doctor.name}${doctor.specialization ? ` - ${doctor.specialization}` : ''}${doctor.doctor_type ? ` (${doctor.doctor_type}` : ''}${doctor.doctor_class ? ` - ${doctor.doctor_class})` : doctor.doctor_type ? ')' : ''}`;
  };

  const handleDoctorSelect = (doctor) => {
    setFormData(prev => ({ ...prev, doctor_id: doctor.id }));
    setDoctorSearch(formatDoctorDisplay(doctor));
    setShowDoctorDropdown(false);
    const savedDiscount = doctor.discount_percentage != null ? doctor.discount_percentage.toString() : '';
    setDiscountPercentage(savedDiscount);
    if (newSale.product_id) {
      const product = products.find(p => p.id === newSale.product_id);
      if (product) {
        setNewSale(prev => ({ ...prev, unit_price: computeUnitPrice(product, savedDiscount) }));
      }
    }
  };

  return (
    <>
      <AddVisit
        formData={formData}
        handleFormChange={handleFormChange}
        handleSubmit={handleSubmit}
        loading={loading}
        canManageSales={canManageSales}
        doctorSearch={doctorSearch}
        setDoctorSearch={setDoctorSearch}
        handleDoctorSearchChange={handleDoctorSearchChange}
        showDoctorDropdown={showDoctorDropdown}
        setShowDoctorDropdown={setShowDoctorDropdown}
        filteredDoctors={filteredDoctors}
        handleDoctorSelect={handleDoctorSelect}
        productSearch={productSearch}
        handleProductSearchChange={handleProductSearchChange}
        showProductDropdown={showProductDropdown}
        setShowProductDropdown={setShowProductDropdown}
        filteredProducts={filteredProducts}
        handleProductSelect={handleProductSelect}
        sales={sales}
        newSale={newSale}
        handleSaleChange={handleSaleChange}
        addSale={addSale}
        removeSale={removeSale}
        totalSalesAmount={totalSalesAmount}
        currentStock={currentStock}
        discountPercentage={discountPercentage}
        handleDiscountChange={handleDiscountChange}
      />
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </>
  );
}

export default AddVisitContainer;